import * as React from 'react';
import { useModal } from '@europrocurement/flexy-components/providers';

/**
 * Interface exposée via la ref pour contrôler la modal depuis l'extérieur
 */
export interface HeaderActionModalHandle {
    /** Ouvre la modal */
    open: () => void;
    /** Ferme la modal */
    close: () => void;
}

/**
 * État du cycle de vie de la modal
 */
export type ModalLifecycleState = 'idle' | 'loading' | 'ready' | 'processing' | 'error';

/**
 * Props du composant HeaderActionModal
 */
export interface HeaderActionModalProps {
    /**
     * Contenu à afficher dans la modal.
     * Reçoit les contrôles de la modal pour permettre la fermeture depuis l'intérieur.
     */
    children: (controls: HeaderActionModalControls) => React.ReactNode;

    /**
     * Contenu à afficher pendant le chargement initial
     */
    loadingContent?: React.ReactNode;

    /**
     * Callback exécuté à l'ouverture de la modal
     * Peut retourner une promesse pour un chargement asynchrone
     */
    onOpen?: () => void | Promise<void>;

    /**
     * Callback exécuté à la fermeture de la modal
     */
    onClose?: () => void;

    /**
     * Callback exécuté quand le traitement démarre
     */
    onProcessingStart?: () => void;

    /**
     * Callback exécuté quand le traitement se termine
     */
    onProcessingEnd?: () => void;
}

/**
 * Contrôles fournis au contenu de la modal
 */
export interface HeaderActionModalControls {
    /** Ferme la modal */
    close: () => void;
    /** Définit l'état de la modal */
    setLifecycleState: (state: ModalLifecycleState) => void;
    /** État actuel de la modal */
    lifecycleState: ModalLifecycleState;
    /** Affiche un contenu de chargement personnalisé */
    showLoading: (content?: React.ReactNode) => void;
}

/**
 * Modal générique pour les actions des en-têtes de listing.
 *
 * Fournit une structure de base pour :
 * - Les formulaires de création d'entités
 * - Les wizards multi-étapes
 * - Les confirmations d'actions
 *
 * La modal est contrôlée via une ref, ce qui permet de l'ouvrir
 * depuis un bouton parent sans couplage fort.
 *
 * @example
 * ```tsx
 * const modalRef = React.useRef<HeaderActionModalHandle>(null);
 *
 * return (
 *     <>
 *         <HeaderActionButton
 *             label="Créer"
 *             onClick={() => modalRef.current?.open()}
 *         />
 *
 *         <HeaderActionModal
 *             ref={modalRef}
 *             onOpen={loadInitialData}
 *             onClose={resetForm}
 *         >
 *             {({ close, setLifecycleState }) => (
 *                 <CreationForm
 *                     onSubmit={async (data) => {
 *                         setLifecycleState('processing');
 *                         await createEntity(data);
 *                         close();
 *                     }}
 *                     onCancel={close}
 *                 />
 *             )}
 *         </HeaderActionModal>
 *     </>
 * );
 * ```
 */
const HeaderActionModal = React.forwardRef<HeaderActionModalHandle, HeaderActionModalProps>(
    (
        {
            children,
            loadingContent,
            onOpen,
            onClose,
            onProcessingStart,
            onProcessingEnd,
        }: HeaderActionModalProps,
        ref,
    ) => {
        const { modalActions } = useModal();

        const [lifecycleState, setLifecycleState] = React.useState<ModalLifecycleState>('idle');
        const isActiveRef = React.useRef(false);
        const previousStateRef = React.useRef<ModalLifecycleState>('idle');

        const handleClose = React.useCallback(() => {
            isActiveRef.current = false;
            setLifecycleState('idle');
            modalActions.close();
            onClose?.();
        }, [modalActions, onClose]);

        const showLoading = React.useCallback(
            (content?: React.ReactNode) => {
                const loadingElement = content ?? loadingContent ?? null;
                if (loadingElement) {
                    modalActions.call(loadingElement);
                }
            },
            [loadingContent, modalActions],
        );

        const controls: HeaderActionModalControls = React.useMemo(
            () => ({
                close: handleClose,
                setLifecycleState,
                lifecycleState,
                showLoading,
            }),
            [handleClose, lifecycleState, showLoading],
        );

        const handleOpen = React.useCallback(async () => {
            isActiveRef.current = true;
            setLifecycleState('loading');

            if (loadingContent) {
                modalActions.call(loadingContent);
            }

            if (onOpen) {
                try {
                    await onOpen();
                } catch (error) {
                    console.error(error);
                    if (isActiveRef.current) {
                        setLifecycleState('error');
                    }
                    return;
                }
            }

            if (isActiveRef.current) {
                setLifecycleState('ready');
            }
        }, [loadingContent, modalActions, onOpen]);

        React.useEffect(() => {
            if (lifecycleState === 'processing' && previousStateRef.current !== 'processing') {
                onProcessingStart?.();
            }

            if (previousStateRef.current === 'processing' && lifecycleState !== 'processing') {
                onProcessingEnd?.();
            }

            previousStateRef.current = lifecycleState;
        }, [lifecycleState, onProcessingStart, onProcessingEnd]);

        React.useEffect(() => {
            if (!isActiveRef.current) return;

            if (lifecycleState === 'ready' || lifecycleState === 'error') {
                modalActions.call(children(controls));
            }
        }, [lifecycleState, children, controls, modalActions]);

        React.useImperativeHandle(
            ref,
            () => ({
                open: handleOpen,
                close: handleClose,
            }),
            [handleOpen, handleClose],
        );

        return null;
    },
);

HeaderActionModal.displayName = 'HeaderActionModal';

export default HeaderActionModal;
